/* eslint-disable jsx-a11y/label-has-associated-control */
import { Button, Dialog, IconButton } from '@material-ui/core';
import { XIcon } from '@phosphor-icons/react';
import { FC, useEffect, useState } from 'react';
import SearchAutocomplete from '../../shared/components/SearchAutocomplete';
import { ServiceOption } from '../../shared/types';
import { BORDER, createAdminPageStyles, MUTED, NAVY, TEXT } from '../../style';
import {
  ADD_PRESTATION_EMPTY,
  ADD_PRESTATION_LABEL,
  ADD_PRESTATION_TITLE,
} from '../constants';

interface AddConsultantPrestationModalProps {
  open: boolean;
  saving?: boolean;
  services: ServiceOption[];
  onClose: () => void;
  onSave: (serviceId: number) => void;
}

const useStyles = createAdminPageStyles({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      maxWidth: 560,
      width: '100%',
      margin: 16,
      padding: '28px 32px 24px',
      boxShadow: '0 16px 40px rgba(14, 18, 71, 0.18)',
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 20,
  },
  dialogTitle: {
    fontFamily: 'Poppins',
    fontSize: 20,
    fontWeight: 600,
    color: '#000B33',
    margin: 0,
    lineHeight: 1.3,
  },
  dialogClose: {
    margin: '-8px -8px 0 0',
    color: TEXT,
    padding: 6,
  },
  field: {
    marginBottom: 24,
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  cancelBtn: {
    background: '#FFFFFF',
    color: NAVY,
    border: `1.5px solid ${BORDER}`,
    textTransform: 'none',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '12px 16px',
    boxShadow: 'none',
    '&:hover': {
      background: '#F7F8FB',
      boxShadow: 'none',
    },
    '&:disabled': {
      color: MUTED,
    },
  },
  confirmBtn: {
    background: '#000A8D',
    color: '#FFFFFF',
    textTransform: 'none',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 15,
    borderRadius: 8,
    padding: '12px 16px',
    boxShadow: 'none',
    '&:hover': {
      background: '#1b2158',
      boxShadow: 'none',
    },
    '&:disabled': {
      background: '#E8EAF0',
      color: MUTED,
    },
  },
});

const AddConsultantPrestationModal: FC<AddConsultantPrestationModalProps> = ({
  open,
  saving = false,
  services,
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const [draftId, setDraftId] = useState<number | ''>('');

  useEffect(() => {
    if (open) {
      setDraftId('');
    }
  }, [open]);

  const selectedService = services.find(item => Number(item.id) === Number(draftId)) || null;

  const handleSave = () => {
    if (!draftId || saving) {
      return;
    }
    onSave(Number(draftId));
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialog}
      aria-labelledby="add-consultant-prestation-title">
      <div className={classes.dialogHeader}>
        <h2 id="add-consultant-prestation-title" className={classes.dialogTitle}>
          {ADD_PRESTATION_TITLE}
        </h2>
        <IconButton
          aria-label="Fermer"
          className={classes.dialogClose}
          onClick={onClose}
          disabled={saving}>
          <XIcon size={20} />
        </IconButton>
      </div>

      <div className={classes.field}>
        <label className={classes.label} htmlFor="add-consultant-prestation-select">
          {ADD_PRESTATION_LABEL}
        </label>
        <SearchAutocomplete
          id="add-consultant-prestation-select"
          options={services}
          value={selectedService}
          placeholder="Rechercher une prestation"
          getOptionLabel={option => option.name}
          noOptionsText={ADD_PRESTATION_EMPTY}
          disabled={saving}
          onChange={selected => {
            const service = selected as ServiceOption | null;
            setDraftId(service ? Number(service.id) : '');
          }}
        />
      </div>

      <div className={classes.actions}>
        <Button className={classes.cancelBtn} onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button
          className={classes.confirmBtn}
          onClick={handleSave}
          disabled={saving || !draftId}>
          {saving ? 'Chargement...' : 'Enregistrer'}
        </Button>
      </div>
    </Dialog>
  );
};

export default AddConsultantPrestationModal;
